/**
 * Chiptune engine: everything is synthesised live with the Web Audio API
 * (2 pulse leads, a pulse arpeggio, a triangle bass, noise drums + echo),
 * sequenced with a look-ahead scheduler. Also exposes a beat clock so the
 * visuals (van bounce, weasel dance) stay locked to the music.
 */

const NOTE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
export function midi(name) {
  const m = /^([A-G])([#b]?)(-?\d)$/.exec(name);
  if (!m) throw new Error('bad note ' + name);
  return 12 * (Number(m[3]) + 1) + NOTE[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
}
const hz = (m) => 440 * Math.pow(2, (m - 69) / 12);

// ---------------------------------------------------------------- the song
const BPM = 132;
const CHORDS = {
  C: { root: 36, arp: [60, 64, 67] },
  G: { root: 43, arp: [55, 59, 62] },
  Am: { root: 45, arp: [57, 60, 64] },
  F: { root: 41, arp: [53, 57, 60] },
  Em: { root: 40, arp: [52, 55, 59] },
  Dm: { root: 38, arp: [50, 53, 57] },
};

// melody: bars of "note:length" in eighth notes (8 per bar)
const SECTIONS = {
  A: {
    chords: ['C', 'G', 'Am', 'F', 'C', 'G', 'F', 'G'],
    melody: [
      'E5:1 G5:1 C6:2 B5:1 G5:1 E5:2',
      'D5:1 G5:1 B5:2 A5:1 G5:1 D5:2',
      'C5:1 E5:1 A5:2 G5:1 E5:1 C5:2',
      'A4:1 C5:1 F5:2 E5:1 D5:1 C5:2',
      'E5:1 G5:1 C6:2 D6:1 C6:1 G5:2',
      'D6:1 B5:1 G5:2 A5:1 B5:1 D6:2',
      'C6:1 A5:1 F5:2 G5:1 A5:1 C6:2',
      'B5:2 A5:1 G5:1 D5:2 G5:2',
    ],
  },
  B: {
    chords: ['F', 'G', 'Em', 'Am', 'Dm', 'G', 'C', 'G'],
    melody: [
      'C6:3 A5:1 F5:2 A5:2',
      'B5:3 G5:1 D5:2 G5:2',
      'G5:2 E5:2 G5:2 B5:2',
      'A5:3 E5:1 C5:2 E5:2',
      'D6:2 A5:2 F5:2 A5:2',
      'D6:3 B5:1 G5:2 B5:2',
      'C6:2 E6:2 G6:2 E6:2',
      'D6:2 B5:2 G5:4',
    ],
  },
};
const ORDER = ['A1', 'A2', 'B', 'A2'];
const VARIANT = { A1: { sec: 'A', harmony: false, arp: true }, A2: { sec: 'A', harmony: true, arp: true }, B: { sec: 'B', harmony: true, arp: true } };
const INTRO_BARS = 2;

const ARP_PATTERN = [0, 1, 2, 3, 2, 1, 2, 1, 0, 1, 2, 3, 2, 3, 2, 1];
const KICK = new Set([0, 8, 10]);
const SNARE = new Set([4, 12]);

const parseMelody = (str) => {
  const out = [];
  let pos = 0;
  for (const tok of str.split(' ')) {
    const [n, l] = tok.split(':');
    const len = Number(l) * 2; // in 16ths
    if (n !== '-') out.push({ step: pos, len, note: midi(n) });
    pos += len;
  }
  return out;
};
for (const sec of Object.values(SECTIONS)) sec.parsed = sec.melody.map(parseMelody);

const SCALE = [0, 2, 4, 5, 7, 9, 11];
const thirdAbove = (m) => {
  const i = SCALE.indexOf(((m % 12) + 12) % 12);
  if (i < 0) return m + 4;
  return m + ((SCALE[(i + 2) % 7] - SCALE[i] + 12) % 12);
};

// ---------------------------------------------------------------- synth voices
const waveCache = new WeakMap();
function pulseWave(ctx, duty) {
  let m = waveCache.get(ctx);
  if (!m) waveCache.set(ctx, (m = new Map()));
  let w = m.get(duty);
  if (!w) {
    const N = 64;
    const re = new Float32Array(N), im = new Float32Array(N);
    for (let n = 1; n < N; n++) {
      re[n] = (2 / (Math.PI * n)) * Math.sin(2 * Math.PI * n * duty);
      im[n] = (2 / (Math.PI * n)) * (1 - Math.cos(2 * Math.PI * n * duty));
    }
    w = ctx.createPeriodicWave(re, im);
    m.set(duty, w);
  }
  return w;
}

function noiseBuffer(ctx) {
  const len = ctx.sampleRate * 2;
  const b = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = b.getChannelData(0);
  let s = 12345;
  for (let i = 0; i < len; i++) {
    s = (s * 1664525 + 1013904223) >>> 0;
    d[i] = (s / 4294967296) * 2 - 1;
  }
  return b;
}

class Voices {
  constructor(ctx) {
    this.ctx = ctx;
    this.noise = noiseBuffer(ctx);
  }

  /** Pulse note with an NES-ish envelope. opts: duty, vib (cents), slideTo (Hz), attack, hold */
  pulse(t, f, dur, vol, dest, o = {}) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.setPeriodicWave(pulseWave(ctx, o.duty ?? 0.5));
    osc.frequency.setValueAtTime(f, t);
    if (o.slideTo) osc.frequency.exponentialRampToValueAtTime(o.slideTo, t + dur);
    const g = ctx.createGain();
    const a = o.attack ?? 0.004;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + a);
    g.gain.exponentialRampToValueAtTime(Math.max(vol * (o.sustain ?? 0.6), 0.0002), t + Math.min(0.12, dur * 0.5));
    g.gain.setValueAtTime(Math.max(vol * (o.sustain ?? 0.6), 0.0002), t + Math.max(dur - 0.03, 0.02));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.02);
    osc.connect(g);
    g.connect(o.pan != null ? this._pan(o.pan, dest) : dest);
    if (o.vib && dur > 0.22) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 5.8;
      const lg = ctx.createGain();
      lg.gain.setValueAtTime(0, t);
      lg.gain.linearRampToValueAtTime(o.vib, t + 0.18);
      lfo.connect(lg);
      lg.connect(osc.detune);
      lfo.start(t);
      lfo.stop(t + dur + 0.05);
    }
    osc.start(t);
    osc.stop(t + dur + 0.05);
    return osc;
  }

  _pan(p, dest) {
    const sp = this.ctx.createStereoPanner();
    sp.pan.value = p;
    sp.connect(dest);
    return sp;
  }

  tri(t, f, dur, vol, dest, o = {}) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(f, t);
    if (o.slideTo) osc.frequency.exponentialRampToValueAtTime(o.slideTo, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.006);
    g.gain.setValueAtTime(vol, t + Math.max(dur - 0.05, 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.01);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + dur + 0.03);
  }

  kick(t, vol, dest) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(170, t);
    osc.frequency.exponentialRampToValueAtTime(42, t + 0.11);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + 0.22);
  }

  noiseHit(t, dur, vol, dest, { type = 'highpass', freq = 6000, q = 0.7, slide = 0 } = {}) {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(freq, t);
    if (slide) f.frequency.exponentialRampToValueAtTime(Math.max(slide, 30), t + dur);
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(f);
    f.connect(g);
    g.connect(dest);
    src.start(t, Math.random() * 1.5);
    src.stop(t + dur + 0.02);
  }

  snare(t, vol, dest) {
    this.noiseHit(t, 0.16, vol, dest, { type: 'bandpass', freq: 2200, q: 0.6 });
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(230, t);
    osc.frequency.exponentialRampToValueAtTime(130, t + 0.07);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.7, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + 0.1);
  }
}

// ---------------------------------------------------------------- the engine
export class ChipAudio {
  constructor() {
    this.ctx = null;
    this.started = false;
    this.muted = false;
    this.night = 0;
    this.t0 = 0;
    this.stepDur = 60 / BPM / 4;
    this.nextStep = 0;
    this.stepIndex = 0;
    this._timer = null;
    this.lastSfx = {};
    this.beatFallback = 0;
    this.onStep = null;
  }

  /** Must be called from a user gesture. */
  start() {
    if (this.started) { this.ctx?.resume?.(); return; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC({ latencyHint: 'interactive' });
    this._build(this.ctx);
    this.started = true;
    this.t0 = this.ctx.currentTime + 0.15;
    this.nextStep = this.t0;
    this.stepIndex = 0;
    this._timer = setInterval(() => this._tick(), 25);
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden) this.ctx.suspend?.(); else if (!this.muted) this.ctx.resume?.();
    });
  }

  _build(ctx, dest = ctx.destination) {
    this.v = new Voices(ctx);
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 20;
    comp.ratio.value = 5;
    comp.attack.value = 0.003;
    comp.release.value = 0.2;
    this.master = ctx.createGain();
    this.master.gain.value = 0.95;
    this.tone = ctx.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.frequency.value = 16000;
    this.master.connect(this.tone);
    this.tone.connect(comp);
    comp.connect(dest);

    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 1;
    this.musicBus.connect(this.master);
    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = 0.9;
    this.sfxBus.connect(this.master);

    // lead echo (dotted 8th)
    this.echoIn = ctx.createGain();
    const delay = ctx.createDelay(1);
    delay.delayTime.value = this.stepDur * 3;
    const fb = ctx.createGain();
    fb.gain.value = 0.32;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3200;
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    this.echoIn.connect(delay);
    delay.connect(lp);
    lp.connect(fb);
    fb.connect(delay);
    lp.connect(wet);
    wet.connect(this.musicBus);
    this.echoWet = wet;

    this.leadBus = ctx.createGain();
    this.leadBus.connect(this.musicBus);
    this.leadBus.connect(this.echoIn);
    this.rhythmBus = ctx.createGain();
    this.rhythmBus.connect(this.musicBus);
    this.bassBus = ctx.createGain();
    this.bassBus.connect(this.musicBus);
  }

  // ---------------------------------------------------------------- transport
  get beat() {
    if (!this.started || !this.ctx) return this.beatFallback;
    const t = this.ctx.currentTime - (this.ctx.outputLatency || 0.02) - this.t0;
    return Math.max(0, (t * BPM) / 60);
  }

  _tick() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    while (this.nextStep < ctx.currentTime + 0.18) {
      this.scheduleStep(this.stepIndex, this.nextStep);
      this.stepIndex++;
      this.nextStep += this.stepDur;
    }
  }

  /** Song position -> { section, bar, step } */
  _locate(stepIndex) {
    const bar = Math.floor(stepIndex / 16);
    const step = stepIndex % 16;
    if (bar < INTRO_BARS) return { intro: true, bar, step, sec: 'A', chord: ['C', 'G'][bar], variant: null };
    const b = bar - INTRO_BARS;
    const secIdx = Math.floor(b / 8) % ORDER.length;
    const barInSec = b % 8;
    const variant = VARIANT[ORDER[secIdx]];
    return { intro: false, bar: barInSec, step, sec: variant.sec, chord: SECTIONS[variant.sec].chords[barInSec], variant, secIdx };
  }

  scheduleStep(stepIndex, t, ctx = this.ctx) {
    const loc = this._locate(stepIndex);
    const v = this.v;
    const night = this.night;
    const chord = CHORDS[loc.chord];
    const sd = this.stepDur;
    const drumsOn = !loc.intro || loc.bar === 1;
    const drumVol = 1 - night * 0.7;

    // ---- arpeggio
    const arpOn = loc.intro || loc.variant.arp;
    if (arpOn) {
      const idx = ARP_PATTERN[loc.step];
      const notes = [chord.arp[0], chord.arp[1], chord.arp[2], chord.arp[0] + 12];
      const accent = loc.step % 4 === 0 ? 1.25 : 1;
      v.pulse(t, hz(notes[idx] + 12), sd * 1.5, 0.036 * accent, this.rhythmBus, { duty: 0.125, pan: loc.step % 2 ? 0.35 : -0.35, sustain: 0.4 });
    }
    const isB = !loc.intro && loc.sec === 'B';
    // ---- bass (root / octave bounce, triangle); the bridge gets a syncopated groove
    if (!loc.intro || loc.bar === 1) {
      if (isB) {
        const hits = { 0: 0, 3: 12, 6: 0, 8: 0, 10: 12, 12: 0, 14: 7 };
        if (hits[loc.step] !== undefined) v.tri(t, hz(chord.root + hits[loc.step]), sd * 1.6, 0.2, this.bassBus);
      } else if (loc.step % 2 === 0) {
        const k = loc.step / 2;
        let m = chord.root;
        if (k % 2 === 1) m += 12;
        if (k === 7) m = chord.root + 7;
        v.tri(t, hz(m), sd * 1.8, 0.2, this.bassBus);
      }
    }
    // ---- drums
    if (drumsOn) {
      const kick = isB ? loc.step === 0 || loc.step === 3 || loc.step === 8 || loc.step === 10 || loc.step === 14 : KICK.has(loc.step);
      if (kick) v.kick(t, 0.36 * drumVol, this.rhythmBus);
      if (SNARE.has(loc.step)) v.snare(t, 0.15 * drumVol, this.rhythmBus);
      if (isB && loc.step === 15) v.snare(t, 0.05 * drumVol, this.rhythmBus);
      if (loc.step % 2 === 0) v.noiseHit(t, loc.step === 14 ? 0.11 : 0.035, (loc.step % 4 === 0 ? 0.05 : 0.035) * drumVol, this.rhythmBus, { freq: 7500 });
      // crash on the first beat of every section
      if (!loc.intro && loc.bar === 0 && loc.step === 0) v.noiseHit(t, 0.7, 0.07 * drumVol, this.rhythmBus, { freq: 5200, q: 0.4 });
      // fill on the last bar of a section
      if (!loc.intro && loc.bar === 7 && loc.step >= 12) v.snare(t, 0.1 * drumVol, this.rhythmBus);
      if (!loc.intro && loc.bar === 7 && loc.step === 15) v.noiseHit(t, 0.25, 0.04, this.rhythmBus, { type: 'bandpass', freq: 500, slide: 6000, q: 0.5 });
    }
    // ---- melody
    if (!loc.intro) {
      const bar = SECTIONS[loc.sec].parsed[loc.bar];
      for (const n of bar) {
        if (n.step !== loc.step) continue;
        const dur = n.len * sd * 0.94;
        v.pulse(t, hz(n.note), dur, 0.105, this.leadBus, { duty: night > 0.5 ? 0.5 : 0.25, vib: 7, sustain: 0.68 });
        if (loc.variant.harmony) {
          v.pulse(t, hz(thirdAbove(n.note) - 12), dur, 0.045, this.leadBus, { duty: 0.5, sustain: 0.6 });
        }
        if (loc.secIdx === 3 || loc.sec === 'B') {
          // sparkle an octave up on longer notes
          if (n.len >= 4) v.pulse(t + sd * 2, hz(n.note + 12), sd * 1.5, 0.03, this.leadBus, { duty: 0.125, sustain: 0.5 });
        }
      }
    }
    if (this.onStep) this.onStep(stepIndex, t, loc);
  }

  // ---------------------------------------------------------------- SFX
  setMuted(m) {
    this.muted = m;
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(m ? 0.0001 : 0.95, t, 0.08);
  }

  setNight(n) {
    this.night = n;
    if (!this.ctx) return;
    this.tone.frequency.setTargetAtTime(16000 - n * 11500, this.ctx.currentTime, 0.5);
    this.echoWet.gain.setTargetAtTime(0.55 + n * 0.35, this.ctx.currentTime, 0.5);
  }

  sfx(name) {
    if (!this.started || this.muted || !this.ctx || this.ctx.state !== 'running') return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const gap = { hop: 0.16, dook: 0.09, boing: 0.2, wee: 0.6, yeet: 1, thud: 0.3, splat: 0.4, dizzy: 0.4, honk: 0.4, jingle: 2, click: 0.03, ding: 0.1 }[name] ?? 0.1;
    if (now - (this.lastSfx[name] || -9) < gap) return;
    this.lastSfx[name] = now;
    const v = this.v, dest = this.sfxBus, t = now + 0.005;
    switch (name) {
      case 'hop':
        v.pulse(t, 330, 0.07, 0.07, dest, { duty: 0.5, slideTo: 520 });
        break;
      case 'dook': {
        const f = 520 + Math.random() * 120;
        v.pulse(t, f, 0.045, 0.1, dest, { duty: 0.25 });
        v.pulse(t + 0.07, f * 1.18, 0.05, 0.1, dest, { duty: 0.25 });
        break;
      }
      case 'boing':
        v.pulse(t, 180, 0.24, 0.13, dest, { duty: 0.5, slideTo: 720, vib: 40, sustain: 0.8 });
        break;
      case 'wee':
        v.pulse(t, 420, 0.42, 0.09, dest, { duty: 0.125, slideTo: 1500, sustain: 0.9 });
        v.tri(t, 210, 0.42, 0.1, dest, { slideTo: 750 });
        break;
      case 'yeet':
        v.pulse(t, 1000, 0.75, 0.1, dest, { duty: 0.5, slideTo: 140, sustain: 0.9 });
        v.noiseHit(t, 0.7, 0.14, dest, { type: 'bandpass', freq: 3000, slide: 300, q: 0.8 });
        break;
      case 'thud':
        v.tri(t, 130, 0.16, 0.35, dest, { slideTo: 45 });
        v.noiseHit(t, 0.06, 0.12, dest, { type: 'lowpass', freq: 1200 });
        break;
      case 'splat':
        v.noiseHit(t, 0.22, 0.2, dest, { type: 'lowpass', freq: 2500, slide: 300 });
        v.tri(t, 320, 0.2, 0.2, dest, { slideTo: 90 });
        break;
      case 'dizzy': {
        for (let i = 0; i < 4; i++) v.pulse(t + i * 0.09, 380 + (i % 2) * 140, 0.09, 0.07, dest, { duty: 0.5 });
        break;
      }
      case 'honk':
        v.pulse(t, 349, 0.13, 0.12, dest, { duty: 0.5 });
        v.pulse(t, 440, 0.13, 0.09, dest, { duty: 0.25 });
        v.pulse(t + 0.16, 349, 0.2, 0.12, dest, { duty: 0.5 });
        v.pulse(t + 0.16, 440, 0.2, 0.09, dest, { duty: 0.25 });
        break;
      case 'jingle': {
        // little ice-cream-van chime
        const seq = [72, 76, 79, 84, 79, 76, 79, 84, 88, 84];
        seq.forEach((m, i) => v.pulse(t + i * 0.11, hz(m), 0.1, 0.09, dest, { duty: 0.25, sustain: 0.5 }));
        break;
      }
      case 'ding':
        v.pulse(t, hz(88), 0.25, 0.08, dest, { duty: 0.5, sustain: 0.3 });
        v.pulse(t + 0.05, hz(95), 0.3, 0.06, dest, { duty: 0.5, sustain: 0.3 });
        break;
      case 'click':
        v.pulse(t, 900, 0.03, 0.06, dest, { duty: 0.5 });
        break;
    }
  }

  // ---------------------------------------------------------------- offline render (for tests)
  /** Renders `seconds` of the song into an AudioBuffer using an OfflineAudioContext. */
  static async renderOffline(seconds = 30, sampleRate = 44100) {
    const ctx = new OfflineAudioContext(2, Math.floor(seconds * sampleRate), sampleRate);
    const a = new ChipAudio();
    a.ctx = ctx;
    a._build(ctx);
    a.started = true;
    a.t0 = 0;
    let i = 0;
    for (let t = 0; t < seconds; t += a.stepDur) a.scheduleStep(i++, t, ctx);
    return ctx.startRendering();
  }
}

export const SONG_INFO = { bpm: BPM, bars: INTRO_BARS + ORDER.length * 8 };
